import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaBox,
  FaUsers,
  FaChartBar,
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaHome,
  FaCog,
  FaChartLine,
} from 'react-icons/fa';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { path: '/admin', icon: FaChartBar, label: 'Dashboard' },
    { path: '/admin/statistics', icon: FaChartLine, label: 'İstatistikler' },
    { path: '/admin/products', icon: FaBox, label: 'Ürün Yönetimi' },
    { path: '/admin/options', icon: FaCog, label: 'Seçenek Yönetimi' },
    { path: '/admin/users', icon: FaUsers, label: 'Kullanıcı Yönetimi' },
    { path: '/admin/orders', icon: FaShoppingCart, label: 'Sipariş Yönetimi' },
  ];

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-white p-3 rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          {sidebarOpen ? (
            <FaTimes className="text-gray-600" />
          ) : (
            <FaBars className="text-gray-600" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`
        fixed left-0 top-0 h-full w-80 bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
      >
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FaChartBar className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
              <p className="text-sm text-gray-500">Yönetim Sistemi</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {/* Ana sayfa butonu */}
          <button
            onClick={() => {
              navigate('/');
              setSidebarOpen(false);
            }}
            className="w-full flex items-center gap-4 px-4 py-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all group"
          >
            <FaHome className="text-lg group-hover:scale-110 transition-transform" />
            <span className="font-medium">Ana Sayfa</span>
          </button>

          <div className="my-4 border-t border-gray-100"></div>

          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = isActiveRoute(item.path);

            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all group
                  ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }
                `}
              >
                <Icon
                  className={`
                  text-lg transition-transform group-hover:scale-110
                  ${isActive ? 'text-white' : ''}
                `}
                />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                )}
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-bold">A</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Admin</p>
                <p className="text-xs text-gray-500">Yönetici</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <div className="lg:ml-80 min-h-screen">
        <main className="p-4 lg:p-8">
          <div className="mb-6 lg:mb-8">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FaChartBar className="text-white text-sm" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{title}</h1>
            </div>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-6 lg:p-8">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
